/** Typed error taxonomy — presentation must not invent success on these. */

export type VetoErrorCode =
  | "VAULT_LOCKED"
  | "VAULT_BAD_PIN"
  | "CRYPTO_OPEN_FAILED"
  | "CRYPTO_SEAL_FAILED"
  | "SESSION_MISSING"
  | "RELAY_REJECTED"
  | "RELAY_TIMEOUT"
  | "GROUP_TOO_LARGE"
  | "GROUP_EPOCH_STALE"
  | "ATTACHMENT_ENCRYPT_FAILED"
  | "PERMISSION_DENIED"
  | "VALIDATION_FAILED"
  | "NOT_IMPLEMENTED";

export class VetoError extends Error {
  readonly code: VetoErrorCode;
  readonly retryable: boolean;

  constructor(code: VetoErrorCode, message: string, opts?: { retryable?: boolean; cause?: unknown }) {
    super(message, opts?.cause !== undefined ? { cause: opts.cause } : undefined);
    this.name = "VetoError";
    this.code = code;
    this.retryable = opts?.retryable ?? false;
  }
}

export function isVetoError(err: unknown): err is VetoError {
  return err instanceof VetoError;
}

const LONG_HEX_RUN = /\b(?:0x)?[0-9a-fA-F]{32,}\b/g;
const LONG_B64_RUN = /\b[A-Za-z0-9+/]{64,}={0,2}\b/g;

/**
 * SEC (SPEC §1.2 minimal metadata / §18): error strings surface in toasts and
 * can be screenshotted or reported — never let them carry key-shaped material
 * (hex keys, base64 blobs, seeds). Validation copy ("Invite is empty.") passes
 * through untouched; only long opaque runs are truncated to first…last 4.
 */
export function scrubKeyMaterial(text: string): string {
  return text
    .replace(LONG_HEX_RUN, (m) => `${m.slice(0, 4)}…${m.slice(-4)}`)
    .replace(LONG_B64_RUN, (m) => `${m.slice(0, 4)}…${m.slice(-4)}`);
}

export function userFacingMessage(err: unknown): string {
  if (isVetoError(err)) {
    switch (err.code) {
      case "VAULT_BAD_PIN":
        return "Wrong PIN — try again";
      case "SESSION_MISSING":
        return "No secure session yet — open the chat again";
      case "RELAY_TIMEOUT":
        return "Relay timed out — message kept in outbox";
      case "GROUP_TOO_LARGE":
        return "Group exceeds the 32-member limit";
      case "GROUP_EPOCH_STALE":
        return "Group key rotated — cannot decrypt";
      case "PERMISSION_DENIED":
        return "Permission denied";
      case "NOT_IMPLEMENTED":
        return "Not available in this build";
      default:
        return scrubKeyMaterial(err.message || "Something went wrong");
    }
  }
  if (err instanceof Error) return scrubKeyMaterial(err.message);
  return "Something went wrong";
}
